/**
 * A page's title block.
 *
 * Title, optional lead paragraph, optional breadcrumb, and actions pushed to
 * the trailing edge. Every page uses it, which is the point: the alternative is
 * each page inventing its own heading margins, and they then disagree.
 *
 * The `<h1>` is 32px here rather than inheriting the base heading size, because
 * the prototype's page titles are a fixed size independent of Tailwind's type
 * scale, and it drops to 26.7px below 920px — a 32px title wraps to three lines
 * on a 375px screen.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.5.4, §4.5
 */
import { cn } from '../../lib/cn';

import { Kicker } from './Kicker';

export interface PageHeadProps {
  title: React.ReactNode;
  /** A short kicker above the title, e.g. the section this page sits in. */
  kicker?: React.ReactNode;
  /** One sentence on what the page is for. */
  lead?: React.ReactNode;
  /** Trailing-edge actions, typically one `action` button plus ghosts. */
  actions?: React.ReactNode;
  className?: string;
}

export function PageHead({
  title,
  kicker,
  lead,
  actions,
  className,
}: PageHeadProps): React.JSX.Element {
  return (
    <div className={cn('mb-s4 flex flex-wrap items-start gap-s3', className)}>
      <div className="min-w-0">
        {kicker !== undefined && <Kicker>{kicker}</Kicker>}
        <h1 className="mt-1.5 text-[26.7px] leading-[1.05] lg:text-[32px]">{title}</h1>
        {lead !== undefined && (
          <p className="text-body mt-1.5 max-w-[640px] text-[13.5px] font-light">
            {lead}
          </p>
        )}
      </div>
      {actions !== undefined && (
        <div className="ml-auto flex flex-wrap items-center gap-s2 pt-3.5">{actions}</div>
      )}
    </div>
  );
}
